/**
 * AI Service
 * Handles AI processing for knowledge base content and generates intelligent responses
 */

export interface AIResponse {
  content: string;
  sources: KnowledgeBaseSource[];
  confidence: number;
}

export interface KnowledgeBaseSource {
  id: string;
  title: string;
  url: string;
  excerpt: string;
  space: string;
  relevanceScore: number;
  type?: 'tutorial';
  category?: string;
  difficulty?: string;
}

class AIService {
  private apiBaseUrl: string;

  constructor() {
    const raw = import.meta.env.VITE_VIAPHOTON_API_URL || '/api';
    let normalized = raw.replace(/\/$/, '');
    const needsApiSuffix = /localhost:3001$/.test(normalized) || /kb-api\./.test(normalized);
    if (needsApiSuffix && !/\/api$/.test(normalized)) {
      normalized += '/api';
    }
    this.apiBaseUrl = normalized;
  }

  /**
   * Process user query and generate AI response with knowledge base content
   */
  async processQuery(query: string): Promise<AIResponse> {
    try {
      // Use knowledge base search (Tutorials only)
      const searchResults = await this.searchAllContent(query);

      // Call backend AI service with search results
      const aiResponse = await this.callBackendAI(query, searchResults.results || []);
      
      return aiResponse;
    } catch (error) {
      console.error('Error processing AI query:', error);
      return {
        content: "I'm sorry, I encountered an error while searching our documentation. Please try again later.",
        sources: [],
        confidence: 0
      };
    }
  }

  /**
   * Search knowledge base content (Tutorials) via backend API
   */
  private async searchAllContent(query: string) {
    const response = await fetch(`${this.apiBaseUrl}/search/all?query=${encodeURIComponent(query)}&limit=10`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Search API error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Call backend AI service with search results
   */
  private async callBackendAI(query: string, results: any[]): Promise<AIResponse> {
    const response = await fetch(`${this.apiBaseUrl}/search/ai/process`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        query,
        searchResults: results
      })
    });

    if (!response.ok) {
      throw new Error(`AI API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Test AI service connection via backend
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/ai/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.connected;
    } catch (error) {
      console.error('AI service connection test failed:', error);
      return false;
    }
  }
}

export const aiService = new AIService();
export default aiService;
